import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, Plus, X, Heart } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";

interface InteractionSearchResult {
  id: string;
  animalId: string;
  eventType: string;
  occurredAt: string;
  author: string | null;
  animalName: string | null;
}

interface InteractionSearchSelectProps {
  selectedIds: string[];
  selectedNames: Map<string, string>;
  onAdd: (id: string, displayName: string) => void;
  onRemove: (id: string) => void;
  enabled?: boolean;
  showLabel?: boolean;
  testIdPrefix?: string;
}

export function InteractionSearchSelect({
  selectedIds,
  selectedNames,
  onAdd,
  onRemove,
  enabled = true,
  showLabel = true,
  testIdPrefix = "search-select",
}: InteractionSearchSelectProps) {
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];

  const { data: searchData } = useQuery<{ interactions: InteractionSearchResult[] }>({
    queryKey: ["/api/tasks/interaction-search?q=" + encodeURIComponent(search)],
    enabled: search.length >= 2 && enabled,
  });

  useEffect(() => {
    if (showSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch]);

  function handleAdd(interaction: InteractionSearchResult) {
    const displayName = `${interaction.eventType} — ${interaction.animalName || interaction.animalId} (${new Date(interaction.occurredAt).toLocaleDateString()})`;
    if (!selectedIds.includes(interaction.id)) {
      onAdd(interaction.id, displayName);
    }
    setSearch("");
    setShowSearch(false);
  }

  return (
    <div className="space-y-2">
      {showLabel && (
        <div className="flex items-center gap-2">
          <Label className="flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Interactions
          </Label>
          {!showSearch && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs text-muted-foreground gap-1"
              onClick={() => setShowSearch(true)}
              data-testid={`${testIdPrefix}-button-add-interaction`}
            >
              <Plus className="w-3 h-3" /> Add
            </Button>
          )}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map(id => (
            <Badge key={id} variant="secondary" className="text-xs" data-testid={`${testIdPrefix}-badge-interaction-${id}`}>
              <Link2 className="w-3 h-3 mr-1" />
              {selectedNames.get(id) || id.slice(0, 8)}
              <button className="ml-1" onClick={() => onRemove(id)}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {(showSearch || !showLabel) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Input
              ref={inputRef}
              placeholder="Search interactions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid={`${testIdPrefix}-input-interaction-search`}
            />
            {showLabel && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => { setShowSearch(false); setSearch(""); }}
                data-testid={`${testIdPrefix}-button-close-interaction-search`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {searchData?.interactions && searchData.interactions.length > 0 && (
            <div className="border rounded-md max-h-32 overflow-y-auto">
              {searchData.interactions
                .filter(i => !selectedIds.includes(i.id))
                .map(i => {
                  const displayName = `${i.eventType} — ${i.animalName || i.animalId} (${new Date(i.occurredAt).toLocaleDateString()})`;
                  return (
                    <button
                      key={i.id}
                      className="w-full text-left px-2 py-1 text-sm hover-elevate"
                      onClick={() => handleAdd(i)}
                      data-testid={`${testIdPrefix}-button-link-interaction-${i.id}`}
                    >
                      {displayName} {i.animalId && followedIds.includes(i.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 inline-block align-middle" data-testid={`heart-followed-interaction-${i.id}`} />}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
